import {
   SystemFieldEnum,
   LeadModel,
   RecordUtils,
   StageStore,
   UserStore,
   UnionFieldModelType,
   Stage,
   LeadTemplateModel,
   ContactTemplateModel,
   LeadStore,
   LeadTemplateType,
   TemplateDetailsStatusEnum,
   LeadTemplatesStore,
   FieldsStore,
   GOFResponse
} from '@flowwai-frontend/shared/sales-crm/sales-crm-core'
import {
   AddAnotherGoFResult,
   FieldResponse,
   SystemFieldEnumResponseResult
} from '@flowwai-frontend/bps-officer-web/graphql'

import { SystemFieldResponsesApiData } from '../types'
import { Common_PipelineItemTemplate_FragmentFragment } from '../fragments/pipelineItemTemplateFragment.generatedTypes'

import { getTransformedFieldResponse } from '../utils/FieldResponseUtils'
import { convertToGoFWithFieldsType } from '../utils/TransformationUtils/TransformationUtils'

import { convertGraphqlFieldResponseToUnionFieldResponse } from './fields'

interface StoreLeadToLeadModelProps {
   contactTemplate: ContactTemplateModel
   leadId: string
   leadTemplateId: string
   leadStore: LeadStore
   systemFieldsResponse: SystemFieldResponsesApiData
}

interface StoreLeadFieldResponsesInLeadFunctionProps {
   fieldResponse: SystemFieldEnumResponseResult
   leadModel: LeadModel
   contactTemplate: ContactTemplateModel
   userStore: UserStore
   stageStore: StageStore
}
export const storeLeadAssigneeInUserStore = (
   transformedFieldResponse: any,
   userStore: UserStore,
   fieldResponse: FieldResponse
) => {
   const isFieldLeadAssigneeField =
      fieldResponse.systemFieldEnum === SystemFieldEnum.LeadAssignee

   if (isFieldLeadAssigneeField) {
      userStore.createOrUpdateUser({
         ...transformedFieldResponse.response
      })
   }
}

export const setFieldResponseInLeadModel = (
   field: UnionFieldModelType,
   leadModel: LeadModel,
   stageStore: StageStore,
   fieldResponse: FieldResponse,
   userStore: UserStore,
   gofResponseId?: string
) => {
   if (!field.gof) throw new Error('Gof is not found for field')

   const transformedFieldResponse = convertGraphqlFieldResponseToUnionFieldResponse(
      field,
      fieldResponse
   )

   const gofId = field.gof.gofId
   const dummyGofResponseId = RecordUtils.getGoFResponseId(
      leadModel.leadId,
      gofId
   )

   const isLeadStage = field.systemFieldEnum === SystemFieldEnum.LeadStage
   if (isLeadStage)
      stageStore.createOrUpdateStage({
         ...(transformedFieldResponse.response as Stage)
      })

   leadModel.createOrUpdateFieldResponse(
      gofId,
      gofResponseId || dummyGofResponseId,
      transformedFieldResponse
   )

   storeLeadAssigneeInUserStore(
      transformedFieldResponse,
      userStore,
      fieldResponse
   )
}

const storeLeadStageInStageStore = (
   transformedFieldResponse: any,
   stageStore: StageStore,
   fieldResponse: FieldResponse
) => {
   const isLeadStageField =
      fieldResponse.systemFieldEnum === SystemFieldEnum.LeadStage

   if (isLeadStageField) {
      stageStore.createOrUpdateStage({
         ...transformedFieldResponse.response
      })
   }
}

export const storeLeadFieldResponsesInLeadModel = (
   props: StoreLeadFieldResponsesInLeadFunctionProps
) => {
   const {
      fieldResponse,
      leadModel,
      contactTemplate,
      userStore,
      stageStore
   } = props

   const isFieldResponseType = fieldResponse.__typename === 'FieldResponse'
   if (!isFieldResponseType) return

   const leadTemplateField = leadModel.leadTemplate.fields.find(
      field => field.fieldId === fieldResponse.fieldId
   )
   const fieldModel =
      leadTemplateField ?? contactTemplate.getField(fieldResponse.fieldId)

   if (!fieldModel.gof) {
      throw new Error(`${fieldModel.fieldId}: GOF is undefined in the Field`)
   }

   const gofId = fieldModel.gof.gofId
   const entityId = leadTemplateField ? leadModel.leadId : leadModel.contactId
   const gofResponseId = RecordUtils.getGoFResponseId(entityId, gofId)

   const transformedFieldResponse = convertGraphqlFieldResponseToUnionFieldResponse(
      fieldModel,
      fieldResponse
   )

   storeLeadAssigneeInUserStore(
      transformedFieldResponse,
      userStore,
      fieldResponse
   )

   storeLeadStageInStageStore(
      transformedFieldResponse,
      stageStore,
      fieldResponse
   )

   leadModel.createOrUpdateFieldResponse(
      gofId,
      gofResponseId,
      transformedFieldResponse
   )
}

export const storeLeadInLeadsStore = (
   props: StoreLeadToLeadModelProps
): LeadModel => {
   const {
      contactTemplate,
      leadId,
      leadStore,
      systemFieldsResponse,
      leadTemplateId
   } = props

   const { contactId } = systemFieldsResponse

   if (!contactId) {
      throw new Error('contactId is not available')
   }

   const contact = {
      contactId: contactId,
      recordId: contactId,
      templateId: contactTemplate.templateId,
      gofResponses: []
   }

   const lead = {
      leadId: leadId,
      contact: contact,
      recordId: leadId,
      templateId: leadTemplateId,
      gofResponses: []
   }

   leadStore.createOrUpdateLead(lead)
   return leadStore.getLead(leadId)
}

export const convertLeadTemplateModelInputType = (
   leadTemplateResponse: Common_PipelineItemTemplate_FragmentFragment
): LeadTemplateType => {
   const {
      pipelineItemTemplateId,
      gofs,
      assigneeFieldsMapping
   } = leadTemplateResponse

   return {
      templateId: pipelineItemTemplateId,
      name: leadTemplateResponse.name,
      gofs: gofs.map(convertToGoFWithFieldsType),
      assigneeFieldsMapping: assigneeFieldsMapping,
      workflows: []
   }
}

export const storeLeadTemplateModelInTemplateStore = (
   leadTemplate: Common_PipelineItemTemplate_FragmentFragment,
   leadTemplateStore: LeadTemplatesStore
) => {
   const templateId = leadTemplate.pipelineItemTemplateId
   const leadTemplateModelInput: LeadTemplateType = convertLeadTemplateModelInputType(
      leadTemplate
   )

   leadTemplateStore.createOrUpdateTemplate(leadTemplateModelInput)

   const leadTemplateModel = leadTemplateStore.getTemplate(templateId)
   leadTemplateModel.setTemplateDetailsStatus(
      TemplateDetailsStatusEnum.FetchedCompleteDetails
   )

   leadTemplateModel.setCanMergeRecords(leadTemplate.canMergeRecords)
}

export const storeAddAnotherGofResponsesInLeadModel = (
   addAnotherGofResponses: AddAnotherGoFResult[],
   leadModel: LeadModel,
   fieldsStore: FieldsStore
): void =>
   addAnotherGofResponses.forEach(gofResponseConfig => {
      gofResponseConfig.gofResponses.forEach(gofResponse => {
         const gofResponseData: GOFResponse = {
            gofId: gofResponse.gofId,
            gofResponseId: gofResponse.gofResponseId
         }

         leadModel.createOrUpdateGOFResponse(gofResponseData)

         gofResponse.fieldResponses.forEach(fieldResponse => {
            const fieldModel = fieldsStore.getField(fieldResponse.fieldId)

            const transformedFieldResponse = getTransformedFieldResponse(
               fieldModel,
               fieldResponse as FieldResponse
            )

            leadModel.createOrUpdateFieldResponse(
               gofResponse.gofId,
               gofResponse.gofResponseId,
               transformedFieldResponse
            )
         })
      })
   })

export const storeLeadBaseDetailsInTemplateStore = (
   templateId: string,
   templateName: string,
   leadTemplateStore: LeadTemplatesStore
): void => {
   leadTemplateStore.createOrUpdateTemplate({
      name: templateName,
      templateId,
      assigneeFieldsMapping: [],
      gofs: [],
      workflows: []
   })
}
